import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CommonModalLayout from '../../form-components/CommonModalLayout';
import CommonModalButton from '../../form-components/CommonModalButton';

const AccountCard = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '14px 16px',
  border: '1px solid #E5E5E5',
  borderRadius: '8px',
  background: '#F3FAFB',
});

const AccountIcon = styled(Box)({
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  background: '#E9F6F7',
  color: '#3196A0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const AccountName = styled(Typography)({
  fontSize: '15px',
  fontWeight: 700,
  color: '#262626',
  wordBreak: 'break-all',
});

const AccountSub = styled(Typography)({
  fontSize: '13px',
  color: '#737373',
  wordBreak: 'break-all',
});

const SectionLabel = styled(Typography)({
  marginTop: '20px',
  marginBottom: '8px',
  fontSize: '13px',
  fontWeight: 700,
  color: '#262626',
});

const EffectList = styled('ul')({
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const EffectItem = styled('li', {
  shouldForwardProp: prop => prop !== 'keep',
})(({ keep }) => ({
  position: 'relative',
  paddingLeft: '14px',
  fontSize: '14px',
  lineHeight: 1.55,
  color: keep ? '#525252' : '#262626',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '9px',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: keep ? '#3196A0' : '#ef0c23',
  },
}));

const MEMBER_EFFECTS = [
  '회원 앱에서 이 센터의 이용 중인 상품·포인트·예약 내역이 보이지 않아요.',
  '앱으로 이 센터 수업을 예약할 수 없어요.',
];
const MEMBER_KEEPS = [
  '이미 잡힌 예약은 그대로 남아요(취소는 센터에서).',
  '센터 출입과 센터에 남은 기록은 그대로예요.',
  '다시 연동하면 다시 볼 수 있어요.',
];
const TEMPORARY_EFFECTS = ['잠재회원과 앱 계정의 연결이 끊어지고, 앱의 센터 연동 목록에서 이 센터가 빠져요.'];
const TEMPORARY_KEEPS = ['앱 계정은 그대로 남아 계속 로그인할 수 있어요.'];
const INVITE_EFFECTS = ['회원 앱에 온 센터 연동 초대가 사라져요.'];
const INVITE_KEEPS = ['센터에 남은 기록은 그대로예요.', '필요하면 다시 초대할 수 있어요.'];

// mode: 'unlink' 연동 해제 / 'cancelInvite' 초대 취소
const MODE_TEXT = {
  unlink: { title: '앱 연동 해제', heading: '해제하면', confirm: '연동 해제', cancel: '취소', dateSuffix: '연동' },
  cancelInvite: { title: '앱 연동 초대 취소', heading: '취소하면', confirm: '초대 취소', cancel: '닫기', dateSuffix: '초대' },
};

/**
 * 회원·잠재회원 상세의 [연동 해제]·[초대 취소] 확인 창(2026-10-23 디자인 정리). 앱 연동 창과 같은 CommonModalLayout 을 쓴다.
 * account: { name, username, date }
 */
const UnlinkConfirmModal = ({ open, onClose, onConfirm, isMember, account, isLoading, mode = 'unlink' }) => {
  const text = MODE_TEXT[mode];
  const isInvite = mode === 'cancelInvite';
  const effects = isInvite ? INVITE_EFFECTS : isMember ? MEMBER_EFFECTS : TEMPORARY_EFFECTS;
  const keeps = isInvite ? INVITE_KEEPS : isMember ? MEMBER_KEEPS : TEMPORARY_KEEPS;
  const sub = [account?.username && `아이디 ${account.username}`, account?.date && `${account.date} ${text.dateSuffix}`]
    .filter(Boolean)
    .join(' · ');
  return (
    <CommonModalLayout
      open={open}
      // 요청 중에는 Esc·바깥 클릭으로 닫히지 않게
      onClose={isLoading ? undefined : onClose}
      disableEscapeKeyDown={isLoading}
      title={text.title}
      width={480}
      footer={
        <>
          <CommonModalButton variant="cancel" onClick={onClose} disabled={isLoading}>
            {text.cancel}
          </CommonModalButton>
          <CommonModalButton variant="delete" onClick={onConfirm} disabled={isLoading}>
            {text.confirm}
          </CommonModalButton>
        </>
      }
    >
      <AccountCard>
        <AccountIcon>
          <LinkOffIcon sx={{ fontSize: 20 }} />
        </AccountIcon>
        <Box sx={{ minWidth: 0 }}>
          <AccountName>{account?.name || '-'}</AccountName>
          {sub && <AccountSub>{sub}</AccountSub>}
        </Box>
      </AccountCard>
      <SectionLabel>{text.heading}</SectionLabel>
      <EffectList>
        {effects.map(line => (
          <EffectItem key={line}>{line}</EffectItem>
        ))}
        {keeps.map(line => (
          <EffectItem key={line} keep>
            {line}
          </EffectItem>
        ))}
      </EffectList>
    </CommonModalLayout>
  );
};

export default UnlinkConfirmModal;
